import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Cart from "@ucs/Icons/Cart";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: ApplicationState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
});

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    compactCountText?: TypographyPresentationProps;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

const iconMap = {
    cart: Cart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const CountBadge = styled.span`
    background-color: ${getColor("primary")};
    color: white;
    border-radius: 100%;
    position: absolute;
    top: -12px;
    right: -12px;
    font-size: 12px;
    min-width: 24px;
    text-align: center;
    padding: 1px;
    border: 2px solid white;
`;

/** Reloading the cart makes totalCountDisplay temporarily unavailable, so retain the previous value for better UX. * */
let retainedTotalCountToDisplay = 0;

const styles = cartLinkStyles;
const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
}) => {
    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart();
        }
    }, [shouldLoadCart]);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    if (isCompactHeaderCart) {
        return (
            <Link
                {...routerLink}
                color={theme.header.linkColor}
                href={cartUrl}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
            >
                <Typography as="span" {...styles.compactCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        );
    }

    return (
        <Link
            {...routerLink}
            href={cartUrl}
            icon={{
                ...routerLink?.icon,
                iconProps: {
                    src: icon,
                    ...routerLink?.icon?.iconProps,
                    color: retainedTotalCountToDisplay > 0 ? "#000" : UCSColors.gray05,
                },
            }}
            data-test-selector="cartLink"
            style={{ position: "relative" }}
        >
            {showLabel && `${translate("Cart")} `}
            {retainedTotalCountToDisplay > 0 ? (
                <CountBadge data-test-selector="cartLinkQuantity">{retainedTotalCountToDisplay}</CountBadge>
            ) : null}
            <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
        </Link>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
